import React, { useEffect, useState } from 'react';
import { Grid, Box, Typography, Button, IconButton, CardMedia } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { isEquityFreeSlab, updatedStrategyDetails, Values } from '../strategyDetailsWithFeeSlabs';
import MFSelectField from '../../../lib/formik/SelectField';
import { MFTextField } from '../../../lib/formik';
import {
  allowOnlyNumbers,
  currencyConversion,
  getApplicantName,
  getInstallmentFrequencies,
  containsEquity,
  containsLiquid,
  checkedAsyes,
  isLumpSumMode,
  checkValidationBasedOnDate,
} from '../../../utils/utilityFunctions';
import { FeeSlabsType, StrategyDetailsType } from '../../../redux-store/types/api-types';
import { SubHeading } from '../components';
import { TextDatePicker } from '../../../lib/DatePickerWithTextFeild';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../../redux-store/reducers';
import { FormikHelpers, useFormikContext } from 'formik';
import moment from 'moment';
import { ConfirmationDialog } from '../../commonComponents';
import UseRadioGroup from '../../../lib/formik/Radio';
import { strategyMode, YesNoMaster } from '../../../utils/constant';
import { useValidationOrConfigHook } from '../../../utils/validationOrConfigHook';

interface StrategyFormProps {
  values: Values;
  setValues: FormikHelpers<Values>['setValues'];
  setFieldValue: FormikHelpers<Values>['setFieldValue'];
  strategyData: Partial<StrategyDetailsType>[];
  newStrategy: Partial<updatedStrategyDetails>;
  feeSlabsData: FeeSlabsType[];
}

interface GenerateDates {
  (startDate: moment.Moment): string[];
}

export const getAvailableStartDates = (
  creationDate: string | Date,
  frequency: string
): string[] => {
  const tplus10: moment.Moment = moment(creationDate).add(10, 'day');

  const generateDates: GenerateDates = (startDate: moment.Moment) => {
    const result: string[] = [];
    const endDate = startDate.clone().add(30, 'days');
    const fixedDays = [1, 5, 10, 15, 20, 25, 30];

    const current = startDate.clone();

    while (current.isSameOrBefore(endDate)) {
      if (fixedDays.includes(current.date())) {
        result.push(current.format('YYYY-MM-DD'));
      }
      current.add(1, 'day');
    }

    return result;
  };

  if (['Monthly', 'Quarterly', 'Fortnightly'].includes(frequency)) {
    const firstAvailableDate =
      tplus10.date() <= 15 ? tplus10.clone().date(20) : moment(tplus10).add(1, 'month').date(1);

    return generateDates(firstAvailableDate);
  } else if (frequency === 'Weekly') {
    if (tplus10.date() <= 15) {
      const nextWeekStart =
        tplus10.day() === 0
          ? tplus10.clone().add(1, 'day')
          : moment(tplus10).add(1, 'week').startOf('week');

      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = nextWeekStart.clone().add(i, 'days');
        return date.day() !== 0 && date.day() !== 6 ? date.format('YYYY-MM-DD') : null;
      }).filter((date): date is string => date !== null);
      return weekDates;
    } else {
      const nextMonthStart =
        tplus10.day() === 0
          ? tplus10.clone().add(1, 'day')
          : moment(tplus10).add(1, 'month').startOf('month');

      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = nextMonthStart.clone().add(i, 'days');
        return date.day() !== 0 && date.day() !== 6 ? date.format('YYYY-MM-DD') : null;
      }).filter((date): date is string => date !== null);
      return weekDates;
    }
  }
  return [];
};

const LiquidAndEquityStrategyDetails: React.FC<StrategyFormProps> = ({
  values,
  setValues,
  setFieldValue,
  strategyData,
  newStrategy,
  feeSlabsData,
}) => {
  const { combineAllStrategyDate } = useValidationOrConfigHook();
  const { application } = useSelector((store: RootStateType) => store.application);
  const equityFeeSlab = isEquityFreeSlab(feeSlabsData);
  const { setFieldTouched } = useFormikContext<Values>();
  const filteredActiveStrategies = values.applicationstrategydetails.filter(
    (str) =>
      str?.isActive &&
      !containsLiquid(str?.strategyName || '') &&
      (containsEquity(str) || isLumpSumMode(str?.investmentMode || ''))
  );
  const isStpLumpsumDateValid = checkValidationBasedOnDate(
    application?.createdAt || '',
    combineAllStrategyDate
  );
  const userClearedStartDate = React.useRef(false);
  const filteredLiquidStrategy = values.applicationstrategydetails.find(
    (str) => containsLiquid(str?.strategyName || '') && str.isActive
  );
  const [openStrategyDelete, setOpenStrategyDelete] = useState<{
    status: boolean;
    index: number;
  }>({ status: false, index: 0 });
  const initialLiquidAmount =
    filteredLiquidStrategy?.investmentAmount ?? values.investmentAmount ?? null;

  // const [stpLiquidStrategy, setSTPLiquidStrategy] = useState<Partial<updatedStrategyDetails>>(
  //   filteredLiquidStrategy
  //     ? {
  //         ...filteredLiquidStrategy,
  //         sno: 0,
  //         investmentAmount: initialLiquidAmount as string | number | null,
  //       }
  //     : {
  //         ...newStrategy,
  //         sno: 0,
  //         investmentMode: isStpLumpsumDateValid ? 'stp' : '',
  //         investmentAmount: initialLiquidAmount as string | number | null,
  //       }
  // );
  const [stpLiquidStrategy, setSTPLiquidStrategy] = useState<Partial<updatedStrategyDetails>>(
    () => ({
      ...(filteredLiquidStrategy ?? newStrategy),
      sno: 0,
      investmentMode: isStpLumpsumDateValid ? 'stp' : '',
      investmentAmount: initialLiquidAmount as string | number | null,
    })
  );

  const [stpActiveStrategies, setSTPActiveStrategies] = useState<Partial<updatedStrategyDetails>[]>(
    filteredActiveStrategies.length > 0
      ? filteredActiveStrategies.map((strategy, i) => ({ ...strategy, sno: i + 1 }))
      : [
          {
            ...newStrategy,
            isActive: true,
            investmentMode: isStpLumpsumDateValid ? 'stp' : '',
            sno: 1,
          },
        ]
  );
  const frequency = values.stpApplicableData?.frequencyOfInstallments;
  const installments = values.stpApplicableData?.numberOfInstallments;
  const [startDate, setStartDate] = React.useState<string | null>(
    values.stpApplicableData?.installmentStartDate
  );
  const [endDate, setEndDate] = React.useState<string | null>(
    values.stpApplicableData?.installmentEndDate
  );
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const prevFrequency = React.useRef<string | null>(null);

  const calculateEndDate = (
    start: string | null,
    frequency: string,
    installments: number
  ): string | null => {
    if (!start || !moment(start).isValid() || !frequency || !installments) return null;
    const unit = frequency === 'Weekly' || frequency === 'Fortnightly' ? 'days' : 'months';
    const multiplier = { Weekly: 7, Fortnightly: 14, Monthly: 1, Quarterly: 3 }[frequency] || 1;
    let endDate = moment(start);
    for (let i = 0; i < installments - 1; i++) {
      endDate = endDate.add(multiplier, unit);
    }
    const day = endDate.day();
    if (day === 0) {
      endDate = endDate.add(1, 'days');
    } else if (day === 6) {
      endDate = endDate.add(2, 'days');
    }
    return endDate.format('YYYY-MM-DD');
  };

  const availableStartDates = React.useMemo(() => {
    return [...getAvailableStartDates(values.currentDate || '', frequency as string)];
  }, [values.currentDate, frequency]);

  const updateStrategyValues = (
    updatedStrategies: Partial<updatedStrategyDetails>[],
    investmentAmountOverride?: number | string | null
  ) => {
    setSTPActiveStrategies(updatedStrategies);
    const totalLumpSumInvestment = updatedStrategies.reduce((sum, str) => {
      return sum + (Number(str.lumpsumInvestmentAmount) || 0);
    }, 0);

    const totalSTPInvestment = updatedStrategies.reduce((sum, str) => {
      return sum + (Number(str.investmentAmount) || 0);
    }, 0);

    setValues((prevValues) => ({
      ...prevValues,
      totalLumpsumInvestmentAmount: isStpLumpsumDateValid ? totalLumpSumInvestment : null,
      investmentAmount:
        investmentAmountOverride !== undefined ? investmentAmountOverride : totalSTPInvestment,
      stpApplicableData: {
        ...prevValues.stpApplicableData,
        installmentStartDate: startDate ?? prevValues.stpApplicableData?.installmentStartDate,
        installmentEndDate: calculateEndDate(
          values.stpApplicableData?.installmentStartDate || '',
          frequency || '',
          installments || 0
        ),
        availableStartDates: availableStartDates,
      },
      applicationstrategydetails: [stpLiquidStrategy, ...updatedStrategies],
    }));
  };

  const handleStrategyChange = (
    index: number,
    selectedStrategyName: string,
    trancheAmount: number,
    numberOfInstallments: number
  ) => {
    const selectedStrategy = strategyData.find((val) => val.strategyName === selectedStrategyName);

    const updatedStrategies = stpActiveStrategies.map((item, idx) => {
      if (idx !== index) return item;
      return {
        ...selectedStrategy,
        isActive: true,
        feeType: item.feeType || 'Fixed',
        allocation: item?.allocation || null,
        trancheAmount,
        sno: idx + 1,
        includeLumspsum: item?.includeLumspsum || '',
        investmentMode: item?.investmentMode,
        lumpsumInvestmentAmount:
          isStpLumpsumDateValid &&
          (checkedAsyes(item?.includeLumspsum || '') || isLumpSumMode(item?.investmentMode || ''))
            ? item?.lumpsumInvestmentAmount || null
            : null,
        investmentAmount: isLumpSumMode(item?.investmentMode || '')
          ? null
          : (Number(trancheAmount) || 0) +
            (Number(item?.installmentAmount) || 0) * (numberOfInstallments ?? 0),
        installmentAmount: isLumpSumMode(item?.investmentMode || '')
          ? null
          : Number.isNaN(Number(item?.installmentAmount))
          ? null
          : Number(item?.installmentAmount),
      };
    });

    const totalLumpSumInvestment = updatedStrategies.reduce((sum, str) => {
      return !isLumpSumMode(str?.investmentMode || '')
        ? sum + 0
        : sum + (Number(str.lumpsumInvestmentAmount) || 0);
    }, 0);

    setSTPLiquidStrategy((prev) => ({
      ...prev,
      lumpsumInvestmentAmount: null,
    }));

    setFieldValue(
      'totalLumpsumInvestmentAmount',
      isStpLumpsumDateValid ? totalLumpSumInvestment : null
    );
    updateStrategyValues(updatedStrategies);
  };

  const handleInputChange = (
    index: number,
    allocation: number,
    trancheAmount: number | null,
    numberOfInstallments: number | null
  ) => {
    const { investmentAmount: liquidInvestment } = stpLiquidStrategy;

    if (trancheAmount && numberOfInstallments) {
      const updatedStrategies = stpActiveStrategies.map((item, idx) => {
        const totalAllocation = Number(
          (
            ((liquidInvestment as number) * (idx === index ? allocation : item.allocation || 0)) /
            100
          ).toFixed(4)
        );
        const installmentAmount = Number(
          ((totalAllocation - trancheAmount) / numberOfInstallments).toFixed(2)
        );
        const investmentAmount = Number(totalAllocation.toFixed(4));

        return {
          ...item,
          allocation: idx === index ? allocation : item.allocation || 0,
          trancheAmount: trancheAmount,
          investmentAmount: isLumpSumMode(item?.investmentMode || '')
            ? null
            : investmentAmount || null,
          installmentAmount: isLumpSumMode(item?.investmentMode || '')
            ? null
            : Number.isNaN(installmentAmount)
            ? null
            : installmentAmount,
        };
      });
      updateStrategyValues(updatedStrategies);
    }
    return;
  };
  const touchInstallmentAmounts = () =>
    stpActiveStrategies.forEach((str, i) => {
      if (!isLumpSumMode(str?.investmentMode || '')) {
        setFieldTouched(`applicationstrategydetails.${i + 1}.installmentAmount`, true, false);
      }
    });
  const [filterItem, setFilterItem] = useState<{
    [key: number]: Partial<updatedStrategyDetails>[];
  }>({});

  const updateFilterItems = (strategies: Partial<updatedStrategyDetails>[]) => {
    const updatedFilterItems = strategies.map((_, index) => {
      const selectedIds = strategies.slice(0, index).map((str) => str.strategyId);
      return strategyData.filter((str) => !selectedIds.includes(str.strategyId));
    });

    setFilterItem(
      updatedFilterItems.reduce(
        (acc, item, idx) => ({
          ...acc,
          [idx]: item,
        }),
        {}
      )
    );
  };

  useEffect(() => {
    updateFilterItems(stpActiveStrategies);

    // When there is only one active equity strategy, allocation must be 100%.
    const activeEquityStrategies = stpActiveStrategies.filter(
      (_item) => containsEquity(_item) && !isLumpSumMode(_item?.investmentMode || '')
    );
    if (activeEquityStrategies.length === 1) {
      const singleEquityIndex = stpActiveStrategies.findIndex(
        (_item) => containsEquity(_item) && !isLumpSumMode(_item?.investmentMode || '')
      );
      if (stpActiveStrategies[singleEquityIndex]?.allocation !== 100) {
        handleInputChange(
          singleEquityIndex,
          100,
          values.stpApplicableData.trancheAmount,
          installments
        );
      }
    }

    setValues((prevValues) => ({
      ...prevValues,
      investmentAmount: stpLiquidStrategy.investmentAmount || null,
      applicationstrategydetails: [stpLiquidStrategy, ...stpActiveStrategies],
      feeSlabdetails:
        prevValues.feeSlabdetails.length > 0 ? prevValues.feeSlabdetails : equityFeeSlab,
    }));
  }, [stpLiquidStrategy, stpActiveStrategies]);

  useEffect(() => {
    const skipAutoFill = userClearedStartDate.current && !startDate;
    if (frequency === 'Weekly' && !skipAutoFill) {
      if (!startDate && availableStartDates.length > 0) {
        setStartDate(availableStartDates[0]);
        setValues((prevValues) => ({
          ...prevValues,
          stpApplicableData: {
            ...prevValues.stpApplicableData,
            installmentStartDate: availableStartDates[0],
            installmentEndDate: calculateEndDate(
              availableStartDates[0],
              frequency || '',
              installments || 0
            ),
          },
        }));
      }
    }

    if (isInitialLoad && startDate && frequency) {
      setIsInitialLoad(false);
      prevFrequency.current = frequency; // so the first clear isn't treated as a frequency change
      return;
    }

    if (
      !skipAutoFill &&
      frequency &&
      availableStartDates.length > 0 &&
      (prevFrequency.current !== frequency || !startDate)
    ) {
      const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : '';

      if (prevFrequency.current !== frequency) {
        setStartDate(availableStartDates[0]);
        setValues((prevValues) => ({
          ...prevValues,
          stpApplicableData: {
            ...prevValues.stpApplicableData,
            installmentStartDate: availableStartDates[0],
            installmentEndDate: calculateEndDate(
              availableStartDates[0],
              frequency || '',
              installments || 0
            ),
          },
        }));
      } else if (!availableStartDates.includes(formattedStartDate)) {
        setStartDate(availableStartDates[0]);
        setValues((prevValues) => ({
          ...prevValues,
          stpApplicableData: {
            ...prevValues.stpApplicableData,
            installmentStartDate: availableStartDates[0],
            installmentEndDate: calculateEndDate(
              availableStartDates[0],
              frequency || '',
              installments || 0
            ),
          },
        }));
      }

      prevFrequency.current = frequency;
    }

    if (startDate && frequency && values.stpApplicableData?.numberOfInstallments) {
      const calculatedEndDate = calculateEndDate(
        startDate.toString(),
        frequency || '',
        installments || 0
      );
      setEndDate(calculatedEndDate);
      setValues((prevValues) => ({
        ...prevValues,
        stpApplicableData: {
          ...prevValues.stpApplicableData,
          installmentStartDate: startDate,
          installmentEndDate: calculatedEndDate,
        },
      }));
    }

    if (installments && values.stpApplicableData?.trancheAmount) {
      stpActiveStrategies.forEach((str, index) => {
        handleInputChange(
          index,
          str.allocation || 100,
          values.stpApplicableData?.trancheAmount,
          values.stpApplicableData?.numberOfInstallments
        );
      });
    }
    return;
  }, [
    startDate,
    values.stpApplicableData?.frequencyOfInstallments,
    values.stpApplicableData?.numberOfInstallments,
    values.stpApplicableData?.trancheAmount,
    availableStartDates,
  ]);

  useEffect(() => {
    if (installments && values.stpApplicableData?.trancheAmount) {
      stpActiveStrategies.forEach((str, index) => {
        handleInputChange(
          index,
          str.allocation || 100,
          values.stpApplicableData?.trancheAmount,
          installments
        );
      });
    }
  }, [stpLiquidStrategy.investmentAmount, values.stpApplicableData?.trancheAmount, installments]);

  const liquidIndex = values?.applicationstrategydetails?.findIndex((str) =>
    containsLiquid(str.strategyName || '')
  );

  return (
    <>
      <Grid container spacing={2} sx={{ alignItems: 'flex-start', mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              flex: 1,
              maxWidth: '100%',
            }}>
            <MFSelectField
              name={`applicationstrategydetails.${liquidIndex}.strategyName`}
              label="Liquid Strategy *"
              items={strategyData
                .filter((str) => containsLiquid(str?.strategyName || ''))
                .map((str) => ({
                  key: str.strategyName || 'defaultKey',
                  value: str.strategyName || 'defaultValue',
                }))}
              disabled={false}
              onChange={(e) => {
                const selectedStrategy = strategyData.find(
                  (val) => val.strategyName === e.target.value
                );
                const newvalue = {
                  ...selectedStrategy,
                  isActive: true,
                };
                setSTPLiquidStrategy((prev) => ({
                  ...prev,
                  ...newvalue,
                  lumpsumInvestmentAmount: null,
                  trancheAmount: null,
                  investmentAmount: prev?.investmentAmount,
                }));
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              flex: 1,
              maxWidth: '100%',
            }}>
            <MFTextField
              name={`investmentAmount`}
              label="Investment Amount (in INR) *"
              placeholder="Enter Investment Amount"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              onChange={(e) => {
                const raw = e.target.value;
                const amount = raw === '' ? null : Number(raw);
                setFieldValue('investmentAmount', amount);
                setSTPLiquidStrategy((prev) => ({
                  ...prev,
                  isActive: true,
                  investmentAmount: amount,
                }));
              }}
              trimOnBlur={false}
            />
            {!!(stpLiquidStrategy && Number(stpLiquidStrategy?.investmentAmount) > 0) && (
              <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                {currencyConversion(stpLiquidStrategy?.investmentAmount)}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      <SubHeading>Installment Details</SubHeading>
      <Grid container spacing={2} sx={{ alignItems: 'flex-start', mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              flex: 1,
              maxWidth: '100%',
            }}>
            <MFTextField
              name={`stpApplicableData.trancheAmount`}
              label="Minimum STP installment (in INR) *"
              placeholder="Enter Minimum STP installment* (in INR)"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              onChange={(e) => {
                setSTPActiveStrategies((prev) =>
                  prev.map((str) => ({
                    ...str,
                    trancheAmount: Number(e.target.value) || null,
                  }))
                );
                setValues({
                  ...values,
                  stpApplicableData: {
                    ...values.stpApplicableData,
                    trancheAmount: Number(e.target.value) || null,
                  },
                });
              }}
              trimOnBlur={false}
              disabled={false}
            />
            {!!(
              values?.stpApplicableData?.trancheAmount &&
              values?.stpApplicableData?.trancheAmount > 0
            ) && (
              <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                {currencyConversion(values.stpApplicableData?.trancheAmount)}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              flex: 1,
              maxWidth: '100%',
            }}>
            <MFTextField
              name={`stpApplicableData.numberOfInstallments`}
              label="Number of installments *"
              placeholder="Enter Number of installments"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
                // Block the decimal point so only whole numbers can be entered.
                if (e.key === '.') {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                // Force a positive whole number (guards against paste / spinners).
                const parsed = Math.floor(Math.abs(Number(e.target.value)));
                const numVal = parsed > 0 ? parsed : null;
                setValues({
                  ...values,
                  stpApplicableData: {
                    ...values.stpApplicableData,
                    numberOfInstallments: numVal,
                  },
                });
              }}
              trimOnBlur={false}
              disabled={false}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ alignItems: 'flex-start', mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              flex: 1,
              maxWidth: '100%',
            }}>
            <MFSelectField
              name={`stpApplicableData.frequencyOfInstallments`}
              label="Frequency of the installments *"
              items={getInstallmentFrequencies()}
              disabled={false}
              onChange={(e) => {
                userClearedStartDate.current = false;
                setIsInitialLoad(false);
                setValues({
                  ...values,
                  stpApplicableData: {
                    ...values.stpApplicableData,
                    frequencyOfInstallments: e.target.value as string,
                  },
                });
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              flex: 1,
              maxWidth: '100%',
            }}>
            <TextDatePicker
              label="Installment start date *"
              inputLabelStyles={{
                transform: 'unset',
                fontSize: 14,
                fontWeight: 500,
                color: 'rgba(0,0,0,0.7)',
              }}
              placeholder="Enter Installment start date"
              name={`stpApplicableData.installmentStartDate`}
              value={startDate}
              onClick={(val) => {
                const newStart = (val as string | null) || null;
                const newEnd = calculateEndDate(newStart, frequency || '', installments || 0);
                userClearedStartDate.current = !newStart;
                setStartDate(newStart);
                setEndDate(newEnd);
                setFieldTouched('stpApplicableData.installmentEndDate', true, false);
                setValues((prev) => ({
                  ...prev,
                  stpApplicableData: {
                    ...prev.stpApplicableData,
                    installmentStartDate: newStart,
                    installmentEndDate: newEnd,
                  },
                }));
              }}
              availableDates={availableStartDates}
              maxDate={
                availableStartDates.length > 0
                  ? new Date(
                      Math.max(...availableStartDates.map((date) => new Date(date).getTime()))
                    )
                  : new Date()
              }
            />
            <TextDatePicker
              label={'Installment end date *'}
              inputLabelStyles={{
                transform: 'unset',
                fontSize: 14,
                fontWeight: 500,
                color: 'rgba(0,0,0,0.7)',
              }}
              placeholder={'Enter Installment end date*'}
              name={`stpApplicableData.installmentEndDate`}
              value={endDate}
              disabled={true}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          width: '100%',
        }}>
        {stpActiveStrategies.map((str, index) => {
          const strategyFieldIndex = index + 1;
          return (
            <>
              <SubHeading
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}>
                {getApplicantName(index + 1, true)}{' '}
                {isLumpSumMode(stpActiveStrategies[index]?.investmentMode || '')
                  ? 'Lumpsum '
                  : 'Equity '}
                Strategy
                {stpActiveStrategies.length > 1 && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center' }}
                    onClick={() => setOpenStrategyDelete({ status: true, index: index })}>
                    <Typography
                      sx={{
                        color: 'rgba(196, 42, 51, 0.8)',
                        fontSize: 12,
                        ml: 'auto',
                        letterSpacing: '-0.2px',
                        cursor: 'pointer',
                      }}>
                      Remove Strategy
                    </Typography>
                    <IconButton
                      onClick={() => setOpenStrategyDelete({ status: true, index: index })}
                      sx={{ p: '2px' }}>
                      <CardMedia
                        component="img"
                        src="/images/delete-red.svg"
                        alt="Delete Icon"
                        sx={{ width: 'unset' }}
                      />
                    </IconButton>
                  </Box>
                )}
              </SubHeading>
              <Grid container spacing={2} sx={{ alignItems: 'flex-start', mb: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      flex: 1,
                      maxWidth: '100%',
                    }}>
                    {filterItem[index] && (
                      <MFSelectField
                        name={`applicationstrategydetails.${strategyFieldIndex}.strategyName`}
                        label="Please select equity strategy*"
                        items={filterItem[index]
                          ?.filter(
                            (s) =>
                              !containsLiquid(s.strategyName || '') && s.strategyType === 'Equity'
                          )
                          .map((strategy) => ({
                            key: strategy.strategyName || `defaultKey`,
                            value: strategy.strategyName || `defaultValue`,
                          }))}
                        value={str.strategyName || ''}
                        onChange={(e) =>
                          handleStrategyChange(
                            index,
                            e.target.value as string,
                            values.stpApplicableData.trancheAmount || 0,
                            values.stpApplicableData.numberOfInstallments || 0
                          )
                        }
                        disabled={false}
                      />
                    )}
                  </Box>
                </Grid>

                {(!isLumpSumMode(stpActiveStrategies[index]?.investmentMode || '') ||
                  !isStpLumpsumDateValid) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          flex: 1,
                          maxWidth: '100%',
                        }}>
                        <MFTextField
                          name={`applicationstrategydetails.${strategyFieldIndex}.trancheAmount`}
                          label="Minimum STP installment (in INR) *"
                          placeholder="Enter Minimum STP installment* (in INR)"
                          type="number"
                          value={stpActiveStrategies[index].trancheAmount}
                          disabled={true}
                        />
                        {!!(
                          values?.stpApplicableData?.trancheAmount &&
                          values?.stpApplicableData?.trancheAmount > 0
                        ) && (
                          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                            {currencyConversion(values.stpApplicableData?.trancheAmount)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          flex: 1,
                          maxWidth: '100%',
                        }}>
                        <MFTextField
                          name={`applicationstrategydetails.${strategyFieldIndex}.allocation`}
                          label="Allocation % *"
                          placeholder="Enter Allocation %"
                          type="number"
                          value={
                            stpActiveStrategies.filter(
                              (_item) =>
                                containsEquity(_item) && !isLumpSumMode(_item?.investmentMode || '')
                            ).length > 1
                              ? stpActiveStrategies[index]?.allocation || null
                              : 100
                          }
                          onChange={(e) => {
                            touchInstallmentAmounts();
                            handleInputChange(
                              index,
                              Number(e.target.value),
                              values.stpApplicableData?.trancheAmount,
                              values.stpApplicableData?.numberOfInstallments
                            );
                          }}
                          onKeyDown={allowOnlyNumbers}
                          trimOnBlur={false}
                          disabled={
                            stpActiveStrategies?.filter((_item) => {
                              return (
                                containsEquity(_item) && !isLumpSumMode(_item?.investmentMode || '')
                              );
                            }).length >= 2
                              ? false
                              : true
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          flex: 1,
                          maxWidth: '100%',
                        }}>
                        <MFTextField
                          name={`applicationstrategydetails.${strategyFieldIndex}.installmentAmount`}
                          label="Installment Amount (in INR) *"
                          placeholder="Enter Installment amount* (in INR)"
                          type="number"
                          value={stpActiveStrategies[index]?.installmentAmount}
                          disabled={true}
                        />
                        {!!(
                          stpActiveStrategies[index]?.installmentAmount &&
                          Number(stpActiveStrategies[index]?.installmentAmount) > 0
                        ) && (
                          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                            {currencyConversion(stpActiveStrategies[index].installmentAmount)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          flex: 1,
                          maxWidth: '100%',
                        }}>
                        <MFTextField
                          name={`applicationstrategydetails.${strategyFieldIndex}.investmentAmount`}
                          label="Investment Amount (in INR) *"
                          placeholder="Enter Investment amount* (in INR)"
                          type="number"
                          value={stpActiveStrategies[index]?.investmentAmount}
                          disabled={
                            !isLumpSumMode(stpActiveStrategies[index]?.investmentMode || '')
                          }
                        />
                        {!!(
                          stpActiveStrategies[index]?.investmentAmount &&
                          Number(stpActiveStrategies[index]?.investmentAmount) > 0
                        ) && (
                          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                            {currencyConversion(stpActiveStrategies[index].investmentAmount)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    {isStpLumpsumDateValid && (
                      <Grid item xs={12} sm={6}>
                        <UseRadioGroup
                          name={`applicationstrategydetails.${strategyFieldIndex}.includeLumspsum`}
                          formLabel="Include as Lumpsum Investment? *"
                          items={Object.entries(YesNoMaster).map(([value, label]) => ({
                            label,
                            value,
                          }))}
                          value={stpActiveStrategies[index]?.includeLumspsum}
                          onChange={({ target: { value } }) => {
                            const updatedStrategies = stpActiveStrategies.map((item, i) =>
                              i === index
                                ? {
                                    ...item,
                                    includeLumspsum: value,
                                    lumpsumInvestmentAmount: checkedAsyes(value)
                                      ? item?.lumpsumInvestmentAmount
                                      : null,
                                    investmentMode: checkedAsyes(value) ? 'both' : 'stp',
                                  }
                                : item
                            );
                            const totalLumpSumInvestment = updatedStrategies.reduce((sum, str) => {
                              return sum + (Number(str.lumpsumInvestmentAmount) || 0);
                            }, 0);

                            setSTPActiveStrategies(updatedStrategies);
                            setFieldValue(
                              'totalLumpsumInvestmentAmount',
                              isStpLumpsumDateValid ? totalLumpSumInvestment : null
                            );
                          }}
                        />
                      </Grid>
                    )}
                  </>
                )}

                {isStpLumpsumDateValid &&
                  (checkedAsyes(stpActiveStrategies[index]?.includeLumspsum || '') ||
                    isLumpSumMode(stpActiveStrategies[index]?.investmentMode || '')) && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          flex: 1,
                          maxWidth: '100%',
                        }}>
                        <MFTextField
                          name={`applicationstrategydetails.${strategyFieldIndex}.lumpsumInvestmentAmount`}
                          label="Lumpsum Investment Amount (in INR) *"
                          placeholder="Enter Lumpsum Investment Amount"
                          type="number"
                          value={stpActiveStrategies[index]?.lumpsumInvestmentAmount ?? ''}
                          onChange={(e) => {
                            const amount = Number(e.target.value) || null;

                            const modifiedStrategies = stpActiveStrategies.map((item, i) => {
                              if (i !== index) return item;

                              return {
                                ...item,
                                lumpsumInvestmentAmount: amount,
                              };
                            });
                            const totalInvestment = modifiedStrategies.reduce((sum, str) => {
                              return sum + (Number(str.lumpsumInvestmentAmount) || 0);
                            }, 0);

                            setSTPActiveStrategies(modifiedStrategies);
                            setFieldValue('totalLumpsumInvestmentAmount', totalInvestment);
                            updateStrategyValues(modifiedStrategies);
                          }}
                        />
                        {!!(
                          stpActiveStrategies[index]?.lumpsumInvestmentAmount &&
                          Number(stpActiveStrategies[index]?.lumpsumInvestmentAmount) > 0
                        ) && (
                          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                            {currencyConversion(stpActiveStrategies[index].lumpsumInvestmentAmount)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </>
          );
        })}
        <Grid container spacing={2} sx={{ alignItems: 'flex-start', mb: 1 }}>
          {stpActiveStrategies.length < 3 && (
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ color: 'primary.main', fontWeight: 500, fontSize: 14, mt: 2 }}
                onClick={() =>
                  setSTPActiveStrategies([
                    ...stpActiveStrategies,
                    {
                      ...newStrategy,
                      investmentMode: 'stp',
                      isActive: true,
                      sno: values?.applicationstrategydetails?.length,
                    },
                  ])
                }>
                Add STP Strategy
              </Button>
            </Grid>
          )}

          {isStpLumpsumDateValid && stpActiveStrategies.length < 3 && (
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ color: 'primary.main', fontWeight: 500, fontSize: 14, mt: 2 }}
                onClick={() =>
                  setSTPActiveStrategies([
                    ...stpActiveStrategies,
                    {
                      ...newStrategy,
                      investmentMode: 'lumpsum',
                      isActive: true,
                      sno: values?.applicationstrategydetails?.length,
                    },
                  ])
                }>
                Add Lumpsum Strategy
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      <ConfirmationDialog
        message={'Are you sure you want to delete strategy ?'}
        open={openStrategyDelete.status}
        setOpen={() => setOpenStrategyDelete({ status: false, index: 0 })}
        onSave={async () => {
          try {
            const updatedStrategies = stpActiveStrategies
              .filter((_, i) => i !== openStrategyDelete.index)
              .map((strategy, i) => ({ ...strategy, sno: i + 1 }));

            // Preserve the existing STP Investment Amount on delete.
            // Deleting a strategy must not reduce the Total STP Investment Amount.
            const preservedInvestmentAmount = values.investmentAmount;

            setSTPActiveStrategies(updatedStrategies);
            updateStrategyValues(updatedStrategies, preservedInvestmentAmount);
            setOpenStrategyDelete({ status: false, index: 0 });
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setOpenStrategyDelete({ status: false, index: 0 })}
      />
    </>
  );
};

export default LiquidAndEquityStrategyDetails;
